import { toast } from "react-toastify"
import writeXlsxFile from "write-excel-file"
import { sortArrayOfObjectsByKey } from './utilities'

const capitalizeFirstLetter = ( str, convertRemainingTo = "none" ) => {
    if ( !str ) return ''
    if ( convertRemainingTo === "lowercase" )
        return str.charAt( 0 ).toUpperCase() + str.slice( 1 )?.toLowerCase()
    else if ( convertRemainingTo === "uppercase" )
        return str.charAt( 0 ).toUpperCase() + str.slice( 1 )?.toUpperCase()
    else
        return str.charAt( 0 ).toUpperCase() + str.slice( 1 )

}

const capitalizeWords = ( str, convertRemainingTo = "none" ) => {
    if ( !str )
        return ''
    return str
        .split( ' ' )
        .map( word => capitalizeFirstLetter( word, convertRemainingTo ) )
        .join( ' ' )
}

const roundToNDecimalPoints = ( number, n = 2 ) => {
    return Math.round( number * Math.pow( 10, n ) ) / Math.pow( 10, n )
}

export const exportAssignmentsAuditReport = async ( facultyStats, last12MonthsStats, name ) => {
    try {

        // REPORT COUNTS (PRE-PROCESSING)
        let aggregation = {}
        let totalAssignments = 0
        let totalSubmissions = 0
        const excelSheets = [], excelSheetsData = []

        facultyStats.forEach( item => {
            const { institute, department, count, submissions_received } = item

            if ( !aggregation[institute] ) {
                aggregation[institute] = {
                    institute,
                    totalAssignments: 0,
                    totalSubmissions: 0,
                    departments: {},
                    departmentsList: []
                }
            }

            // Add to institute total
            aggregation[institute].totalAssignments += +count
            aggregation[institute].totalSubmissions += +submissions_received

            // Add to department under that institute
            if ( !aggregation[institute].departments[department] ) {
                aggregation[institute].departments[department] = {}
                aggregation[institute].departments[department].totalAssignments = 0
                aggregation[institute].departments[department].totalSubmissions = 0
                aggregation[institute].departmentsList.push( department )
            }

            aggregation[institute].departments[department].totalAssignments += +count
            aggregation[institute].departments[department].totalSubmissions += +submissions_received

            totalAssignments += +count
            totalSubmissions += +submissions_received
        } )
        aggregation = sortArrayOfObjectsByKey( Object.values( aggregation ), 'totalAssignments', -1, "number" )

        // FACULTY-WISE REPORT DATA
        let facultyWiseStatsReportRows = [[
            { fontWeight: 'bold', value: "SL. NO." },
            { value: "FACULTY NAME", fontWeight: 'bold' },
            { fontWeight: 'bold', value: "EMPCODE" },
            { value: "DEPARTMENT", fontWeight: 'bold' },
            { value: "ASSIGNMENTS", align: "right", fontWeight: 'bold', },
            { value: "SUBMISSIONS RECEIVED", align: "right", fontWeight: 'bold', }]
        ]

        for ( let i = 0; i < facultyStats.length; i++ ) {
            const record = facultyStats[i]
            let row = []
            row.push( { value: i + 1, type: Number } )
            row.push( { value: capitalizeWords( record.faculty_name, "lowercase" ), type: String } )
            row.push( { value: record.assignment_created_by, type: String } )
            row.push( { value: `${record?.institute}-${record?.department?.toUpperCase()}`, type: String } )
            row.push( { value: +record.count, type: Number } )
            row.push( { value: +record.submissions_received, type: Number } )
            facultyWiseStatsReportRows.push( row )
        }
        facultyWiseStatsReportRows.push( [{ value: "TOTAL", fontWeight: "bold", span: 4, align: "right" }, null, null, null, { value: totalAssignments, type: Number, fontWeight: "bold" }, { value: totalSubmissions, type: Number, fontWeight: "bold" }] )
        excelSheets.push( "Faculty-wise-stats" )
        excelSheetsData.push( facultyWiseStatsReportRows )


        // INSTITUTE AND DEPARTMENT-WISE STATS REPORT
        const granualityReportRows = [[
            { value: 'INSTITUTE', fontWeight: 'bold', width: 20 },
            { value: 'DEPARTMENT', fontWeight: 'bold', width: 20 },
            { value: 'ASSIGNMENTS', fontWeight: 'bold', align: "right", width: 20 },
            { value: 'SUBMISSIONS', fontWeight: 'bold', align: "right", width: 20 }
        ]]

        for ( const instituteData of aggregation ) {
            // Add each department row
            let firstRow = true
            const departmentAndDownloads = sortArrayOfObjectsByKey( instituteData.departmentsList?.map( dept => ( { dept, totalSubmissions: +instituteData.departments[dept].totalSubmissions } ) ), 'totalSubmissions', -1, 'number' )

            for ( const { dept } of departmentAndDownloads ) {
                const deptData = instituteData.departments[dept]
                granualityReportRows.push( [
                    firstRow ? { value: instituteData.institute, rowSpan: instituteData.departmentsList.length, alignVertical: "center", fontWeight: "bold" } : null,
                    { value: dept?.toUpperCase() },
                    { value: `${deptData.totalAssignments} (${roundToNDecimalPoints( ( deptData.totalAssignments * 100 ) / instituteData.totalAssignments )}%)`, align: "right" },
                    { value: `${deptData.totalSubmissions} (${roundToNDecimalPoints( ( deptData.totalSubmissions * 100 ) / instituteData.totalSubmissions )}%)`, align: "right" }
                ] )
                if ( firstRow )
                    firstRow = !firstRow
            }

            // Add subtotal row
            granualityReportRows.push( [
                { value: '' },
                { value: `${instituteData.institute} TOTAL`, fontWeight: 'bold' },
                { value: `${instituteData.totalAssignments} ( ${roundToNDecimalPoints( ( instituteData.totalAssignments * 100 ) / totalAssignments )}% )`, type: String, fontWeight: 'bold', align: "right" },
                { value: `${instituteData.totalSubmissions} ( ${roundToNDecimalPoints( ( instituteData.totalSubmissions * 100 ) / totalSubmissions )}% )`, type: String, fontWeight: 'bold', align: "right" },
            ], [] )

        }

        // Add grand total row
        granualityReportRows.push( [
            { value: '' },
            { value: 'GRAND TOTAL', fontWeight: 'bold' },
            { value: totalAssignments, type: Number, fontWeight: 'bold' },
            { value: totalSubmissions, type: Number, fontWeight: 'bold' }
        ] )
        excelSheets.push( "Institute-department-wise-stats" )
        excelSheetsData.push( granualityReportRows )


        /*
        // LAST 12 MONTHS INSTITUTE-WISE RESPORT
        */
        if ( last12MonthsStats ) {
            const last12MonthsStatsRows = [[{ value: "Last 12 months activity in each institute", align: "right", fontWeight: "bold", span: 3 }, null, null]]
            for ( const institute in last12MonthsStats ) {
                last12MonthsStatsRows.push(
                    [{ value: institute, fontWeight: "bold", align: "center", span: 3, backgroundColor: "#FFA500" }, null, null],
                    [{ value: "MONTH", fontWeight: "bold" }, { value: "ASSIGNMENTS CREATED", fontWeight: "bold", align: "right" }, { value: "SUBMISSIONS", fontWeight: "bold", align: "right" },]
                )
                for ( const month in last12MonthsStats[institute] ) {
                    if ( Object.prototype.hasOwnProperty.call( last12MonthsStats[institute], month ) ) {
                        const monthStats = last12MonthsStats[institute][month]
                        last12MonthsStatsRows.push( [
                            { value: month, fontWeight: "bold", fontStyle: "italic" },
                            { value: monthStats.assignments_created, type: Number },
                            { value: monthStats.submissions_made, type: Number },
                        ] )
                    }
                }
                last12MonthsStatsRows.push( [] )
            }
            excelSheets.push( "Last-12-months-stats" )
            excelSheetsData.push( last12MonthsStatsRows )
        }

        const excelData = await writeXlsxFile(
            excelSheetsData,
            {
                buffer: true,
                stickyRowsCount: 1,
                sheets: excelSheets,
            } )
        const blob = new Blob( [excelData] )
        const a = document.createElement( 'a' )
        a.download = `${name}.xlsx`
        a.href = window.URL.createObjectURL( blob )
        a.click()
    } catch ( err ) {
        console.log( err )
        toast( "Ooops! Something went wrong while exporting the report!" )
    }
}

export const exportMeetingsAuditReport = async ( facultyStats, last12MonthsStats, name ) => {
    try {

        // REPORT COUNTS (PRE-PROCESSING)
        let aggregation = {}
        let totalMeetings = 0
        const excelSheets = [], excelSheetsData = []

        facultyStats.forEach( item => {
            const { institute, department, meetings_count } = item

            if ( !aggregation[institute] ) {
                aggregation[institute] = {
                    institute,
                    totalMeetings: 0,
                    departments: {},
                    departmentsList: []
                }
            }

            // Add to institute total
            aggregation[institute].totalMeetings += +meetings_count

            // Add to department under that institute
            if ( !aggregation[institute].departments[department] ) {
                aggregation[institute].departments[department] = {}
                aggregation[institute].departments[department].totalMeetings = 0
                aggregation[institute].departmentsList.push( department )
            }

            aggregation[institute].departments[department].totalMeetings += +meetings_count

            totalMeetings += +meetings_count
        } )
        aggregation = sortArrayOfObjectsByKey( Object.values( aggregation ), 'totalMeetings', -1, "number" )

        // FACULTY-WISE REPORT DATA
        let facultyWiseStatsReportRows = [[
            { fontWeight: 'bold', value: "SL. NO." },
            { value: "FACULTY NAME", fontWeight: 'bold' },
            { fontWeight: 'bold', value: "EMPCODE" },
            { value: "DEPARTMENT", fontWeight: 'bold' },
            { value: "MEETINGS", align: "right", fontWeight: 'bold', },
        ]]

        for ( let i = 0; i < facultyStats.length; i++ ) {
            const record = facultyStats[i]
            let row = []
            row.push( { value: i + 1, type: Number } )
            row.push( { value: capitalizeWords( record.creator_data?.name || record.created_by, "lowercase" ), type: String } )
            row.push( { value: record.created_by, type: String } )
            row.push( { value: `${record?.institute}-${record?.department?.toUpperCase()}`, type: String } )
            row.push( { value: +record.meetings_count, type: Number } )
            facultyWiseStatsReportRows.push( row )
        }
        facultyWiseStatsReportRows.push( [{ value: "TOTAL", fontWeight: "bold", span: 4, align: "right" }, null, null, null, { value: totalMeetings, type: Number, fontWeight: "bold" }] )
        excelSheets.push( "Faculty-wise-stats" )
        excelSheetsData.push( facultyWiseStatsReportRows )


        // INSTITUTE AND DEPARTMENT-WISE STATS REPORT
        const granualityReportRows = [[
            { value: 'INSTITUTE', fontWeight: 'bold', width: 20 },
            { value: 'DEPARTMENT', fontWeight: 'bold', width: 20 },
            { value: 'MEETINGS', fontWeight: 'bold', align: "right", width: 20 },
            { value: 'SUBMISSIONS', fontWeight: 'bold', align: "right", width: 20 }
        ]]

        for ( const instituteData of aggregation ) {
            // Add each department row
            let firstRow = true
            const departmentAndDownloads = sortArrayOfObjectsByKey( instituteData.departmentsList?.map( dept => ( { dept, totalMeetings: +instituteData.departments[dept].totalMeetings } ) ), 'totalMeetings', -1, 'number' )

            for ( const { dept } of departmentAndDownloads ) {
                const deptData = instituteData.departments[dept]
                granualityReportRows.push( [
                    firstRow ? { value: instituteData.institute, rowSpan: instituteData.departmentsList.length, alignVertical: "center", fontWeight: "bold" } : null,
                    { value: dept?.toUpperCase() },
                    { value: `${deptData.totalMeetings} (${roundToNDecimalPoints( ( deptData.totalMeetings * 100 ) / instituteData.totalMeetings )}%)`, align: "right" },
                ] )
                if ( firstRow )
                    firstRow = !firstRow
            }

            // Add subtotal row
            granualityReportRows.push( [
                { value: '' },
                { value: `${instituteData.institute} TOTAL`, fontWeight: 'bold' },
                { value: `${instituteData.totalMeetings} ( ${roundToNDecimalPoints( ( instituteData.totalMeetings * 100 ) / totalMeetings )}% )`, type: String, fontWeight: 'bold', align: "right" },
            ], [] )

        }

        // Add grand total row
        granualityReportRows.push( [
            { value: '' },
            { value: 'GRAND TOTAL', fontWeight: 'bold' },
            { value: totalMeetings, type: Number, fontWeight: 'bold' },
        ] )
        excelSheets.push( "Institute-department-wise-stats" )
        excelSheetsData.push( granualityReportRows )


        /*
        // LAST 12 MONTHS INSTITUTE-WISE RESPORT
        */
        if ( last12MonthsStats ) {
            const last12MonthsStatsRows = [[{ value: "Last 12 months activity in each institute", align: "right", fontWeight: "bold", span: 3 }, null, null]]
            for ( const institute in last12MonthsStats ) {
                last12MonthsStatsRows.push(
                    [{ value: institute, fontWeight: "bold", align: "center", span: 3, backgroundColor: "#FFA500" }, null, null],
                    [{ value: "MONTH", fontWeight: "bold" }, { value: "MEETINGS CREATED", fontWeight: "bold", align: "right" }, { value: "PARTICIPANTS", fontWeight: "bold", align: "right" },]
                )
                for ( const month in last12MonthsStats[institute] ) {
                    if ( Object.prototype.hasOwnProperty.call( last12MonthsStats[institute], month ) ) {
                        const monthStats = last12MonthsStats[institute][month]
                        last12MonthsStatsRows.push( [
                            { value: month, fontWeight: "bold", fontStyle: "italic" },
                            { value: monthStats.meetings_created, type: Number },
                            { value: monthStats.participants, type: Number },
                        ] )
                    }
                }
                last12MonthsStatsRows.push( [] )
            }
            excelSheets.push( "Last-12-months-stats" )
            excelSheetsData.push( last12MonthsStatsRows )
        }

        const excelData = await writeXlsxFile(
            excelSheetsData,
            {
                buffer: true,
                stickyRowsCount: 1,
                sheets: excelSheets,
            } )
        const blob = new Blob( [excelData] )
        const a = document.createElement( 'a' )
        a.download = `${name}.xlsx`
        a.href = window.URL.createObjectURL( blob )
        a.click()
    } catch ( err ) {
        console.log( err )
        toast( "Ooops! Something went wrong while exporting the report!" )
    }
}

export const exportExamsAuditReport = async ( data, last12MonthsStats, name ) => {
    try {
        /*
        // REPORT COUNTS (PRE-PROCESSING)
        */
        let aggregation = {}
        let totalExams = 0
        let totalAttempts = 0

        const excelSheets = [], excelSheetsData = []

        data.forEach( item => {
            let { institute = "GLOBAL ASSESSMENTS", department = "ADMISSIONS", count, attempts } = item
            institute = institute || "GLOBAL ASSESSMENTS"
            department = department || "ADMISSIONS"

            if ( !aggregation[institute] ) {
                aggregation[institute] = {
                    institute,
                    totalExams: 0,
                    totalAttempts: 0,
                    departments: {},
                    departmentsList: []
                }
            }

            // Add to institute total
            aggregation[institute].totalExams += +count
            aggregation[institute].totalAttempts += +attempts

            // Add to department under that institute
            if ( !aggregation[institute].departments[department] ) {
                aggregation[institute].departments[department] = {}
                aggregation[institute].departments[department].totalExams = 0
                aggregation[institute].departments[department].totalAttempts = 0
                aggregation[institute].departmentsList.push( department )
            }

            aggregation[institute].departments[department].totalExams += +count
            aggregation[institute].departments[department].totalAttempts += +attempts

            totalExams += +count
            totalAttempts += +attempts
        } )
        aggregation = sortArrayOfObjectsByKey( Object.values( aggregation ), 'totalExams', -1, "number" )
        /*
        // REPORT COUNTS (PRE-PROCESSING-ENDS)
        */


        /*
        // FACULTY STATS ROWS
        */
        let facultyWiseStatsReportRows = [[
            { fontWeight: 'bold', value: "Sl. No." },
            { value: "FACULTY NAME", fontWeight: 'bold' },
            { fontWeight: 'bold', value: "EMPCODE" },
            { value: "DEPARTMENT", fontWeight: 'bold' },
            { value: "EXAMS", fontWeight: 'bold', align: "right" },
            { value: "ATTEMPTS", fontWeight: 'bold', align: "right" }
        ]]

        for ( let i = 0; i < data.length; i++ ) {
            const record = data[i]
            let row = []
            row.push( { value: i + 1, type: Number } )
            row.push( { value: capitalizeWords( record.faculty_name || "ADMIN", "lowercase" ), type: String } )
            row.push( { value: record.created_by, type: String } )
            row.push( { value: `${record?.institute || "GLOBAL ASSESSMENTS"}-${record?.department?.toUpperCase() || "ADMISSIONS"}`, type: String } )
            row.push( { value: +record.count, type: Number } )
            row.push( { value: +record.attempts, type: Number } )
            facultyWiseStatsReportRows.push( row )
        }
        facultyWiseStatsReportRows.push( [{ value: "TOTAL", fontWeight: "bold", span: 4, align: "right" }, null, null, null, { value: totalExams, type: Number, fontWeight: "bold" }, { value: totalExams, type: Number, fontWeight: "bold" }] )
        excelSheets.push( "Faculty-wise-stats" )
        excelSheetsData.push( facultyWiseStatsReportRows )


        // INSTITUTE AND DEPARTMENT-WISE STATS REPORT
        const granualityReportRows = [[
            { value: 'INSTITUTE', fontWeight: 'bold', width: 20 },
            { value: 'DEPARTMENT', fontWeight: 'bold', width: 20 },
            { value: 'EXAMS', fontWeight: 'bold', align: "right", width: 20 },
            { value: 'ATTEMPTS', fontWeight: 'bold', align: "right", width: 20 }]]

        for ( const instituteData of aggregation ) {
            // Add each department row
            let firstRow = true
            const departmentAndDownloads = sortArrayOfObjectsByKey( instituteData.departmentsList?.map( dept => ( { dept, totalAttempts: +instituteData.departments[dept].totalAttempts } ) ), 'totalAttempts', -1, 'number' )

            for ( const { dept } of departmentAndDownloads ) {
                const deptData = instituteData.departments[dept]
                granualityReportRows.push( [
                    firstRow ? { value: instituteData.institute, rowSpan: instituteData.departmentsList.length, alignVertical: "center", fontWeight: "bold" } : null,
                    { value: dept?.toUpperCase() },
                    { value: `${deptData.totalExams} (${roundToNDecimalPoints( ( deptData.totalExams * 100 ) / instituteData.totalExams )}%)`, align: "right" },
                    { value: `${deptData.totalAttempts} (${roundToNDecimalPoints( ( deptData.totalAttempts * 100 ) / instituteData.totalAttempts )}%)`, align: "right" }
                ] )
                if ( firstRow )
                    firstRow = !firstRow
            }

            // Add subtotal row
            granualityReportRows.push( [
                { value: '' },
                { value: `${instituteData.institute} TOTAL`, fontWeight: 'bold' },
                { value: `${instituteData.totalExams} ( ${roundToNDecimalPoints( ( instituteData.totalExams * 100 ) / totalExams )}% )`, type: String, fontWeight: 'bold', align: "right" },
                { value: `${instituteData.totalAttempts} ( ${roundToNDecimalPoints( ( instituteData.totalAttempts * 100 ) / totalAttempts )}% )`, type: String, fontWeight: 'bold', align: "right" },
            ], [] )

        }

        // Add grand total row
        granualityReportRows.push( [
            { value: '' },
            { value: 'GRAND TOTAL', fontWeight: 'bold' },
            { value: totalExams, type: Number, fontWeight: 'bold' },
            { value: totalAttempts, type: Number, fontWeight: 'bold' }
        ] )
        excelSheets.push( "Institute-department-wise-stats" )
        excelSheetsData.push( granualityReportRows )

        /*
        // LAST 12 MONTHS INSTITUTE-WISE RESPORT
        */
        if ( last12MonthsStats ) {
            const last12MonthsStatsRows = [[{ value: "Last 12 months activity in each institute", align: "right", fontWeight: "bold", span: 3 }, null, null]]
            for ( const institute in last12MonthsStats ) {
                last12MonthsStatsRows.push(
                    [{ value: institute, fontWeight: "bold", align: "center", span: 3, backgroundColor: "#FFA500" }, null, null],
                    [{ value: "MONTH", fontWeight: "bold" }, { value: "EXAMS", fontWeight: "bold", align: "right" }, { value: "ATTEMPTS", fontWeight: "bold", align: "right" },]
                )
                for ( const month in last12MonthsStats[institute] ) {
                    if ( Object.prototype.hasOwnProperty.call( last12MonthsStats[institute], month ) ) {
                        const monthStats = last12MonthsStats[institute][month]
                        last12MonthsStatsRows.push( [
                            { value: month, fontWeight: "bold", fontStyle: "italic" },
                            { value: monthStats.exams, type: Number },
                            { value: monthStats.attempts, type: Number },
                        ] )
                    }
                }
                last12MonthsStatsRows.push( [] )
            }
            excelSheets.push( "Last-12-months-stats" )
            excelSheetsData.push( last12MonthsStatsRows )
        }

        const excelData = await writeXlsxFile(
            excelSheetsData,
            {
                sheets: excelSheets,
                stickyRowsCount: 1,
                buffer: true
            }
        )

        const blob = new Blob( [excelData] )
        const a = document.createElement( 'a' )
        a.download = `${name}.xlsx`
        a.href = window.URL.createObjectURL( blob )
        a.click()
    } catch ( err ) {
        console.log( err )
        toast( "Ooops! Something went wrong while exporting the report!" )
    }
}

export const exportQuizzesAuditReport = async ( facultyStats, last12MonthsStats, name ) => {
    try {
        // REPORT COUNTS (PRE-PROCESSING)
        let aggregation = {}
        let totalQuizzes = 0
        let totalAttempts = 0
        const excelSheets = [], excelSheetsData = []

        facultyStats.forEach( item => {
            let { institute = "GLOBAL ASSESSMENTS", department = "ADMISSIONS", count, attempts } = item
            institute = institute || "GLOBAL ASSESSMENTS"
            department = department || "ADMISSIONS"

            if ( !aggregation[institute] ) {
                aggregation[institute] = {
                    institute,
                    totalQuizzes: 0,
                    totalAttempts: 0,
                    departments: {},
                    departmentsList: []
                }
            }

            // Add to institute total
            aggregation[institute].totalQuizzes += +count
            aggregation[institute].totalAttempts += +attempts

            // Add to department under that institute
            if ( !aggregation[institute].departments[department] ) {
                aggregation[institute].departments[department] = {}
                aggregation[institute].departments[department].totalQuizzes = 0
                aggregation[institute].departments[department].totalAttempts = 0
                aggregation[institute].departmentsList.push( department )
            }

            aggregation[institute].departments[department].totalQuizzes += +count
            aggregation[institute].departments[department].totalAttempts += +attempts

            totalQuizzes += +count
            totalAttempts += +attempts
        } )
        aggregation = sortArrayOfObjectsByKey( Object.values( aggregation ), 'totalQuizzes', -1, "number" )
        /*
        // REPORT COUNTS (PRE-PROCESSING-ENDS)
        */

        /*
        // FACULTY STATS ROWS
        */
        let facultyWiseStatsReportRows = [[
            { fontWeight: 'bold', value: "SL. NO." },
            { value: "FACULTY NAME", fontWeight: 'bold' },
            { fontWeight: 'bold', value: "EMPCODE" },
            { value: "DEPARTMENT", fontWeight: 'bold' },
            { value: "QUIZZES", fontWeight: 'bold', align: "right" },
            { value: "ATTEMPTS", fontWeight: 'bold', align: "right" }
        ]]

        for ( let i = 0; i < facultyStats.length; i++ ) {
            const record = facultyStats[i]
            let row = []
            row.push( { value: i + 1, type: Number } )
            row.push( { value: capitalizeWords( record.faculty_name, "lowercase" ), type: String } )
            row.push( { value: record.created_by, type: String } )
            row.push( { value: `${record?.institute}-${record?.department?.toUpperCase()}`, type: String } )
            row.push( { value: +record.count, type: Number } )
            row.push( { value: +record.attempts, type: Number } )
            facultyWiseStatsReportRows.push( row )
        }
        facultyWiseStatsReportRows.push( [{ value: "TOTAL", fontWeight: "bold", span: 4, align: "right" }, null, null, null, { value: totalQuizzes, type: Number, fontWeight: "bold" }, { value: totalQuizzes, type: Number, fontWeight: "bold" }] )
        excelSheets.push( "Faculty-wise-stats" )
        excelSheetsData.push( facultyWiseStatsReportRows )


        // INSTITUTE AND DEPARTMENT-WISE STATS REPORT
        const granualityReportRows = [[
            { value: 'INSTITUTE', fontWeight: 'bold', width: 20 },
            { value: 'DEPARTMENT', fontWeight: 'bold', width: 20 },
            { value: 'QUIZZES', fontWeight: 'bold', align: "right", width: 20 },
            { value: 'ATTEMPTS', fontWeight: 'bold', align: "right", width: 20 }
        ]]

        for ( const instituteData of aggregation ) {
            // Add each department row
            let firstRow = true
            const departmentAndDownloads = sortArrayOfObjectsByKey( instituteData.departmentsList?.map( dept => ( { dept, totalAttempts: +instituteData.departments[dept].totalAttempts } ) ), 'totalAttempts', -1, 'number' )

            for ( const { dept } of departmentAndDownloads ) {
                const deptData = instituteData.departments[dept]
                granualityReportRows.push( [
                    firstRow ? { value: instituteData.institute, rowSpan: instituteData.departmentsList.length, alignVertical: "center", fontWeight: "bold" } : null,
                    { value: dept?.toUpperCase() },
                    { value: `${deptData.totalQuizzes} (${roundToNDecimalPoints( ( deptData.totalQuizzes * 100 ) / instituteData.totalQuizzes )}%)`, align: "right" },
                    { value: `${deptData.totalAttempts} (${roundToNDecimalPoints( ( deptData.totalAttempts * 100 ) / instituteData.totalAttempts )}%)`, align: "right" }
                ] )
                if ( firstRow )
                    firstRow = !firstRow
            }

            // Add subtotal row
            granualityReportRows.push( [
                { value: '' },
                { value: `${instituteData.institute} TOTAL`, fontWeight: 'bold' },
                { value: `${instituteData.totalQuizzes} ( ${roundToNDecimalPoints( ( instituteData.totalQuizzes * 100 ) / totalQuizzes )}% )`, type: String, fontWeight: 'bold', align: "right" },
                { value: `${instituteData.totalAttempts} ( ${roundToNDecimalPoints( ( instituteData.totalAttempts * 100 ) / totalAttempts )}% )`, type: String, fontWeight: 'bold', align: "right" },
            ], [] )

        }

        // Add grand total row
        granualityReportRows.push( [
            { value: '' },
            { value: 'GRAND TOTAL', fontWeight: 'bold' },
            { value: totalQuizzes, type: Number, fontWeight: 'bold' },
            { value: totalAttempts, type: Number, fontWeight: 'bold' }
        ] )
        excelSheets.push( "Institute-department-wise-stats" )
        excelSheetsData.push( granualityReportRows )

        /*
        // LAST 12 MONTHS INSTITUTE-WISE RESPORT
        */
        if ( last12MonthsStats ) {
            const last12MonthsStatsRows = [[{ value: "Last 12 months activity in each institute", align: "right", fontWeight: "bold", span: 3 }, null, null]]
            for ( const institute in last12MonthsStats ) {
                last12MonthsStatsRows.push(
                    [{ value: institute, fontWeight: "bold", align: "center", span: 3, backgroundColor: "#FFA500" }, null, null],
                    [{ value: "MONTH", fontWeight: "bold" }, { value: "QUESTION BANKS CREATED", fontWeight: "bold", align: "right" }, { value: "QUESTIONS ADDED", fontWeight: "bold", align: "right" },]
                )
                for ( const month in last12MonthsStats[institute] ) {
                    if ( Object.prototype.hasOwnProperty.call( last12MonthsStats[institute], month ) ) {
                        const monthStats = last12MonthsStats[institute][month]
                        last12MonthsStatsRows.push( [
                            { value: month, fontWeight: "bold", fontStyle: "italic" },
                            { value: monthStats.quizzes_created, type: Number },
                            { value: monthStats.quiz_attempts_made, type: Number },
                        ] )
                    }
                }
                last12MonthsStatsRows.push( [] )
            }
            excelSheets.push( "Last-12-months-stats" )
            excelSheetsData.push( last12MonthsStatsRows )
        }

        const excelData = await writeXlsxFile(
            excelSheetsData,
            {
                sheets: excelSheets,
                stickyRowsCount: 1,
                buffer: true
            } )

        const blob = new Blob( [excelData] )
        const a = document.createElement( 'a' )
        a.download = `${name}.xlsx`
        a.href = window.URL.createObjectURL( blob )
        a.click()
    } catch ( err ) {
        console.log( err )
        toast( "Ooops! Something went wrong while exporting the report!" )
    }
}

export const exportQuestionBanksAuditReport = async ( facultyStats, last12MonthsStats, name ) => {
    try {
        /*
        // REPORT COUNTS (PRE-PROCESSING)
        */
        let aggregation = {}
        let totalQuestionBanks = 0
        let totalQuestions = 0
        const excelSheets = [], excelSheetsData = []

        facultyStats.forEach( item => {
            const { institute, department, count, questions } = item

            if ( !aggregation[institute] ) {
                aggregation[institute] = {
                    institute,
                    totalQuestionBanks: 0,
                    totalQuestions: 0,
                    departments: {},
                    departmentsList: []
                }
            }

            // Add to institute total
            aggregation[institute].totalQuestionBanks += +count
            aggregation[institute].totalQuestions += +questions

            // Add to department under that institute
            if ( !aggregation[institute].departments[department] ) {
                aggregation[institute].departments[department] = {}
                aggregation[institute].departments[department].totalQuestionBanks = 0
                aggregation[institute].departments[department].totalQuestions = 0
                aggregation[institute].departmentsList.push( department )
            }

            aggregation[institute].departments[department].totalQuestionBanks += +count
            aggregation[institute].departments[department].totalQuestions += +questions

            totalQuestionBanks += +count
            totalQuestions += +questions
        } )
        aggregation = sortArrayOfObjectsByKey( Object.values( aggregation ), 'totalQuestionBanks', -1, "number" )
        /*
        // REPORT COUNTS (PRE-PROCESSING-ENDS)
        */

        /*
        // FACULTY STATS ROWS
        */
        let facultyWiseStatsReportRows = [[
            { fontWeight: 'bold', value: "SL. NO." },
            { value: "FACULTY NAME", fontWeight: 'bold' },
            { fontWeight: 'bold', value: "EMPCODE" },
            { value: "DEPARTMENT", fontWeight: 'bold' },
            { value: "QUESTION BANKS", fontWeight: 'bold', align: "right" },
            { value: "QUESTIONS", fontWeight: 'bold', align: "right" }
        ]]
        for ( let i = 0; i < facultyStats.length; i++ ) {
            const record = facultyStats[i]
            let row = []
            row.push( { value: i + 1, type: Number } )
            row.push( { value: capitalizeWords( record.faculty_name, "lowercase" ), type: String } )
            row.push( { value: record.created_by, type: String } )
            row.push( { value: `${record?.institute}-${record?.department?.toUpperCase()}`, type: String } )
            row.push( { value: +record.count, type: Number } )
            row.push( { value: +record.questions, type: Number } )
            facultyWiseStatsReportRows.push( row )
        }
        facultyWiseStatsReportRows.push( [{ value: "TOTAL", fontWeight: "bold", span: 4, align: "right" }, null, null, null, { value: totalQuestionBanks, type: Number, fontWeight: "bold" }, { value: totalQuestions, type: Number, fontWeight: "bold" }] )
        excelSheets.push( "Faculty-wise-stats" )
        excelSheetsData.push( facultyWiseStatsReportRows )

        /*
        // INSTITUTE AND DEPARTMENT-WISE STATS REPORT
        */
        const granualityReportRows = [[{ value: 'INSTITUTE', fontWeight: 'bold', width: 20 }, { value: 'DEPARTMENT', fontWeight: 'bold', width: 20 }, { value: 'QUESTION BANKS', fontWeight: 'bold', align: "right", width: 20 }, { value: 'QUESTIONS', fontWeight: 'bold', align: "right", width: 20 }]]

        for ( const instituteData of aggregation ) {
            // Add each department row
            let firstRow = true
            const departmentAndDownloads = sortArrayOfObjectsByKey( instituteData.departmentsList?.map( dept => ( { dept, totalQuestions: +instituteData.departments[dept].totalQuestions } ) ), 'totalQuestions', -1, 'number' )

            for ( const { dept } of departmentAndDownloads ) {
                const deptData = instituteData.departments[dept]
                granualityReportRows.push( [
                    firstRow ? { value: instituteData.institute, rowSpan: instituteData.departmentsList.length, alignVertical: "center", fontWeight: "bold" } : null,
                    { value: dept?.toUpperCase() },
                    { value: `${deptData.totalQuestionBanks} (${roundToNDecimalPoints( ( deptData.totalQuestionBanks * 100 ) / instituteData.totalQuestionBanks )}%)`, align: "right" },
                    { value: `${deptData.totalQuestions} (${roundToNDecimalPoints( ( deptData.totalQuestions * 100 ) / instituteData.totalQuestions )}%)`, align: "right" }
                ] )
                if ( firstRow )
                    firstRow = !firstRow
            }

            // Add subtotal row
            granualityReportRows.push( [
                { value: '' },
                { value: `${instituteData.institute} TOTAL`, fontWeight: 'bold' },
                { value: `${instituteData.totalQuestionBanks} ( ${roundToNDecimalPoints( ( instituteData.totalQuestionBanks * 100 ) / totalQuestionBanks )}% )`, type: String, fontWeight: 'bold', align: "right" },
                { value: `${instituteData.totalQuestions} ( ${roundToNDecimalPoints( ( instituteData.totalQuestions * 100 ) / totalQuestions )}% )`, type: String, fontWeight: 'bold', align: "right" },
            ], [] )

        }

        // Add grand total row
        granualityReportRows.push( [
            { value: '' },
            { value: 'GRAND TOTAL', fontWeight: 'bold' },
            { value: totalQuestionBanks, type: Number, fontWeight: 'bold' },
            { value: totalQuestions, type: Number, fontWeight: 'bold' }
        ] )
        excelSheets.push( "Institute-department-wise-stats" )
        excelSheetsData.push( granualityReportRows )

        /*
        // LAST 12 MONTHS INSTITUTE-WISE RESPORT
        */
        if ( last12MonthsStats ) {
            const last12MonthsStatsRows = [[{ value: "Last 12 months activity in each institute", align: "right", fontWeight: "bold", span: 3 }, null, null]]
            for ( const institute in last12MonthsStats ) {
                last12MonthsStatsRows.push(
                    [{ value: institute, fontWeight: "bold", align: "center", span: 3, backgroundColor: "#FFA500" }, null, null],
                    [{ value: "MONTH", fontWeight: "bold" }, { value: "QUESTION BANKS CREATED", fontWeight: "bold", align: "right" }, { value: "QUESTIONS ADDED", fontWeight: "bold", align: "right" },]
                )
                for ( const month in last12MonthsStats[institute] ) {
                    if ( Object.prototype.hasOwnProperty.call( last12MonthsStats[institute], month ) ) {
                        const monthStats = last12MonthsStats[institute][month]
                        last12MonthsStatsRows.push( [
                            { value: month, fontWeight: "bold", fontStyle: "italic" },
                            { value: monthStats.question_banks_created, type: Number },
                            { value: monthStats.questions_added, type: Number },
                        ] )
                    }
                }
                last12MonthsStatsRows.push( [] )
            }
            excelSheets.push( "Last-12-months-stats" )
            excelSheetsData.push( last12MonthsStatsRows )
        }


        const excelData = await writeXlsxFile(
            excelSheetsData,
            {
                sheets: excelSheets,
                stickyRowsCount: 1,
                buffer: true
            } )
        const blob = new Blob( [excelData] )
        const a = document.createElement( 'a' )
        a.download = `${name}.xlsx`
        a.href = window.URL.createObjectURL( blob )
        a.click()
    } catch ( err ) {
        console.log( err )
        toast( "Ooops! Something went wrong while exporting the report!" )
    }
}

export const exportStudyMaterialsAuditReport = async ( data, last12MonthsStats, name ) => {
    try {
        /*
        // REPORT COUNTS (PRE-PROCESSING)
        */
        let aggregation = {}
        let totalMaterials = 0
        let totalAttachments = 0
        let totalDownloads = 0

        const excelSheets = []
        const excelSheetsData = []

        data.forEach( item => {
            const { institute, department, material_count, downloads, attachments } = item

            if ( !aggregation[institute] ) {
                aggregation[institute] = {
                    institute,
                    totalMaterials: 0,
                    totalAttachments: 0,
                    totalDownloads: 0,
                    departments: {},
                    departmentsList: []
                }
            }

            // Add to institute total
            aggregation[institute].totalMaterials += +material_count
            aggregation[institute].totalAttachments += +attachments
            aggregation[institute].totalDownloads += +downloads

            // Add to department under that institute
            if ( !aggregation[institute].departments[department] ) {
                aggregation[institute].departments[department] = {}
                aggregation[institute].departments[department].totalMaterials = 0
                aggregation[institute].departments[department].totalAttachments = 0
                aggregation[institute].departments[department].totalDownloads = 0
                aggregation[institute].departmentsList.push( department )
            }

            aggregation[institute].departments[department].totalMaterials += +material_count
            aggregation[institute].departments[department].totalAttachments += +attachments
            aggregation[institute].departments[department].totalDownloads += +downloads
        } )
        aggregation = sortArrayOfObjectsByKey( Object.values( aggregation ), 'totalMaterials', -1, "number" )
        /*
        // REPORT COUNTS (PRE-PROCESSING-ENDS)
        */

        /*
        // FACULTY STATS ROWS
        */
        const facultyStatsReportRows = [[
            { value: "SL. No.", fontWeight: "bold" },
            { value: "FACULTY NAME", width: 30, fontWeight: "bold" },
            { value: "EMPCODE", width: 10, fontWeight: "bold" },
            { value: "DEPARTMENT", width: 40, fontWeight: "bold" },
            { value: "MATERIALS", width: 14, fontWeight: "bold", align: "right" },
            { value: "ATTACHMENTS", width: 14, fontWeight: "bold", align: "right" },
            { value: "DOWNLOADS", width: 14, fontWeight: "bold", align: "right" },
        ]]

        for ( let i = 0; i < data.length; i++ ) {
            const record = data[i]
            let row = []
            row.push( { value: i + 1, type: Number } )
            row.push( { value: capitalizeWords( record.faculty_name, "lowercase" ), type: String } )
            row.push( { value: record.auid, type: String } )
            row.push( { value: `${record?.institute}-${record?.department?.toUpperCase()}`, type: String } )
            row.push( { value: +record.material_count, type: Number } )
            row.push( { value: +record.attachments, type: Number } )
            row.push( { value: +record.downloads, type: Number } )

            totalMaterials += +( record.material_count || 0 )
            totalAttachments += +( record.attachments || 0 )
            totalDownloads += +( record.downloads || 0 )

            facultyStatsReportRows.push( row )
        }

        facultyStatsReportRows.push( [{ value: null }, { value: "TOTAL", fontWeight: "bold" }, { value: "" }, { value: "" }, { value: totalMaterials, type: Number, fontWeight: "bold" }, { value: totalAttachments, type: Number, fontWeight: "bold" }, { value: totalDownloads, type: Number, fontWeight: "bold" }] )
        excelSheets.push( "Faculty-wise-stats" )
        excelSheetsData.push( facultyStatsReportRows )

        /*
        // INSTITUTE AND DEPARTMENT-WISE STATS REPORT
        */
        const granualityReportRows = [[
            { value: 'INSTITUTE', fontWeight: 'bold', width: 20 },
            { value: 'DEPARTMENT', fontWeight: 'bold', width: 20 },
            { value: 'MATERIALS', fontWeight: 'bold', width: 20, align: "right" },
            { value: 'ATTACHMENTS', fontWeight: 'bold', width: 20, align: "right" },
            { value: 'DOWNLOADS', fontWeight: 'bold', width: 20, align: "right" }
        ]]

        for ( const instituteData of aggregation ) {
            // Add each department row
            let firstRow = true
            const departmentAndDownloads = sortArrayOfObjectsByKey( instituteData.departmentsList?.map( dept => ( { dept, totalDownloads: +instituteData.departments[dept].totalDownloads } ) ), 'totalDownloads', -1, 'number' )

            for ( const { dept } of departmentAndDownloads ) {
                const deptData = instituteData.departments[dept]
                granualityReportRows.push( [
                    firstRow ? { value: instituteData.institute, rowSpan: instituteData.departmentsList.length, alignVertical: "center", fontWeight: "bold" } : null,
                    { value: dept?.toUpperCase() },
                    { value: `${deptData.totalMaterials} (${roundToNDecimalPoints( ( deptData.totalMaterials * 100 ) / instituteData.totalMaterials )}%)`, align: "right" },
                    { value: `${deptData.totalAttachments} (${roundToNDecimalPoints( ( deptData.totalAttachments * 100 ) / instituteData.totalAttachments )}%)`, align: "right" },
                    { value: `${deptData.totalDownloads} (${roundToNDecimalPoints( ( deptData.totalDownloads * 100 ) / instituteData.totalDownloads )}%)`, align: "right" }
                ] )
                if ( firstRow )
                    firstRow = !firstRow
            }

            // Add subtotal row
            granualityReportRows.push( [
                { value: '' },
                { value: `${instituteData.institute} TOTAL`, fontWeight: 'bold' },
                { value: `${instituteData.totalMaterials} ( ${roundToNDecimalPoints( ( instituteData.totalMaterials * 100 ) / totalMaterials )}% )`, type: String, fontWeight: 'bold', align: "right" },
                { value: `${instituteData.totalAttachments} ( ${roundToNDecimalPoints( ( instituteData.totalAttachments * 100 ) / totalAttachments )}% )`, type: String, fontWeight: 'bold', align: "right" },
                { value: `${instituteData.totalDownloads} ( ${roundToNDecimalPoints( ( instituteData.totalDownloads * 100 ) / totalDownloads )}% )`, type: String, fontWeight: 'bold', align: "right" },
            ], [] )

        }

        // Add grand total row
        granualityReportRows.push( [
            { value: '' },
            { value: 'GRAND TOTAL', fontWeight: 'bold' },
            { value: totalMaterials, type: Number, fontWeight: 'bold' },
            { value: totalAttachments, type: Number, fontWeight: 'bold' },
            { value: totalDownloads, type: Number, fontWeight: 'bold' }
        ] )
        excelSheets.push( "Institute-department-wise-stats" )
        excelSheetsData.push( granualityReportRows )

        /*
        // INSTITUTE-WISE LAST 12 MONTHS STATS REPORT
        */
        if ( last12MonthsStats ) {
            const last12MonthsStatsRows = [[{ value: "Last 12 months activity in each institute", align: "right", fontWeight: "bold", span: 3 }, null, null]]
            for ( const institute in last12MonthsStats ) {
                last12MonthsStatsRows.push(
                    [{ value: institute, fontWeight: "bold", align: "center", span: 3, backgroundColor: "#FFA500" }, null, null],
                    [{ value: "MONTH", fontWeight: "bold" }, { value: "STUDY MATERIALS", fontWeight: "bold", align: "right" }, { value: "RESOURCES ADDED", fontWeight: "bold", align: "right" },]
                )
                for ( const month in last12MonthsStats[institute] ) {
                    if ( Object.prototype.hasOwnProperty.call( last12MonthsStats[institute], month ) ) {
                        const monthStats = last12MonthsStats[institute][month]
                        last12MonthsStatsRows.push( [
                            { value: month, fontWeight: "bold", fontStyle: "italic" },
                            { value: monthStats.study_materials, type: Number },
                            { value: monthStats.resources_added, type: Number },
                        ] )
                    }
                }
                last12MonthsStatsRows.push( [] )
            }
            excelSheets.push( "Last-12-months-stats" )
            excelSheetsData.push( last12MonthsStatsRows )
        }

        const excelData = await writeXlsxFile(
            excelSheetsData,
            {
                stickyRowsCount: 1,
                buffer: true,
                sheets: excelSheets
            }
        )
        const blob = new Blob( [excelData] )
        const a = document.createElement( 'a' )
        a.download = `${name}.xlsx`
        a.href = window.URL.createObjectURL( blob )
        a.click()
    } catch ( err ) {
        console.log( err )
        toast( "Ooops! Something went wrong while exporting the report!" )
    }
}

export const exportClassSessionsAuditReport = async ( userStats, last12MonthsStats, name ) => {
    try {
        /*
        // REPORT COUNTS (PRE-PROCESSING)
        */
        let aggregation = {}, totalClassSessions = 0, totalResources = 0
        const excelSheets = [], excelSheetsData = []


        userStats.forEach( item => {
            const { institute, department_name: department, live_sessions_count, resources_count } = item

            if ( !aggregation[institute] ) {
                aggregation[institute] = {
                    institute,
                    totalClassSessions: 0,
                    totalResources: 0,
                    departments: {},
                    departmentsList: []
                }
            }

            // Add to institute total
            aggregation[institute].totalClassSessions += +live_sessions_count
            aggregation[institute].totalResources += +resources_count

            // Add to department under that institute
            if ( !aggregation[institute].departments[department] ) {
                aggregation[institute].departments[department] = {}
                aggregation[institute].departments[department].totalClassSessions = 0
                aggregation[institute].departments[department].totalResources = 0
                aggregation[institute].departmentsList.push( department )
            }

            aggregation[institute].departments[department].totalClassSessions += +live_sessions_count
            aggregation[institute].departments[department].totalResources += +resources_count

            totalClassSessions += +live_sessions_count
            totalResources += +resources_count
        } )
        aggregation = sortArrayOfObjectsByKey( Object.values( aggregation ), 'totalClassSessions', -1, "number" )
        /*
        // REPORT COUNTS (PRE-PROCESSING-ENDS)
        */

        /*
        // FACULTY STATS ROWS
        */
        let facultyStatsReportRows = [[{ fontWeight: 'bold', value: "Sl. No." }, { value: "Faculty name", fontWeight: 'bold' }, { fontWeight: 'bold', value: "EMPCODE" }, { value: "Department", fontWeight: 'bold' }, { value: "Live Sessions", fontWeight: 'bold' }, { value: "Resources", fontWeight: 'bold' }]]
        for ( let i = 0; i < userStats.length; i++ ) {
            const record = userStats[i]
            let row = []
            row.push( { value: i + 1, type: Number } )
            row.push( { value: capitalizeWords( record.faculty_name, "lowercase" ), type: String } )
            row.push( { value: record.empcode, type: String } )
            row.push( { value: `${record?.institute}-${record?.department_name?.toUpperCase()}`, type: String } )
            row.push( { value: +record.live_sessions_count, type: Number } )
            row.push( { value: +record.resources_count, type: Number } )
            facultyStatsReportRows.push( row )
        }
        facultyStatsReportRows.push( [{ value: "TOTAL", fontWeight: "bold", span: 4, align: "right" }, null, null, null, { value: totalClassSessions, type: Number, fontWeight: "bold" }, { value: totalResources, type: Number, fontWeight: "bold" }] )
        excelSheets.push( "Faculty-wise-stats" )
        excelSheetsData.push( facultyStatsReportRows )

        /*
        // INSTITUTE AND DEPARTMENT-WISE STATS REPORT
        */
        const granualityReportRows = [[
            { value: 'INSTITUTE', fontWeight: 'bold', width: 20 },
            { value: 'DEPARTMENT', fontWeight: 'bold', width: 20 },
            { value: 'CLASS SESSIONS', fontWeight: 'bold', align: "right", width: 20 },
            { value: 'RESOURCES LINKED', fontWeight: 'bold', align: "right", width: 20 }
        ]]

        for ( const instituteData of aggregation ) {
            // Add each department row
            let firstRow = true
            const departmentAndDownloads = sortArrayOfObjectsByKey( instituteData.departmentsList?.map( dept => ( { dept, totalResources: +instituteData.departments[dept].totalResources } ) ), 'totalResources', -1, 'number' )

            for ( const { dept } of departmentAndDownloads ) {
                const deptData = instituteData.departments[dept]
                granualityReportRows.push( [
                    firstRow ? { value: instituteData.institute, rowSpan: instituteData.departmentsList.length, alignVertical: "center", fontWeight: "bold" } : null,
                    { value: dept?.toUpperCase() },
                    { value: `${deptData.totalClassSessions} (${roundToNDecimalPoints( ( deptData.totalClassSessions * 100 ) / instituteData.totalClassSessions )}%)`, align: "right" },
                    { value: `${deptData.totalResources} (${roundToNDecimalPoints( ( deptData.totalResources * 100 ) / instituteData.totalResources )}%)`, align: "right" }
                ] )
                if ( firstRow )
                    firstRow = !firstRow
            }

            // Add subtotal row
            granualityReportRows.push( [
                { value: '' },
                { value: `${instituteData.institute} TOTAL`, fontWeight: 'bold' },
                { value: `${instituteData.totalClassSessions} ( ${roundToNDecimalPoints( ( instituteData.totalClassSessions * 100 ) / totalClassSessions )}% )`, type: String, fontWeight: 'bold', align: "right" },
                { value: `${instituteData.totalResources} ( ${roundToNDecimalPoints( ( instituteData.totalResources * 100 ) / totalResources )}% )`, type: String, fontWeight: 'bold', align: "right" },
            ], [] )

        }

        // Add grand total row
        granualityReportRows.push( [
            { value: '' },
            { value: 'GRAND TOTAL', fontWeight: 'bold' },
            { value: totalClassSessions, type: Number, fontWeight: 'bold' },
            { value: totalResources, type: Number, fontWeight: 'bold' }
        ] )
        excelSheets.push( "Institute-department-wise-stats" )
        excelSheetsData.push( granualityReportRows )

        /*
        // LAST 12 MONTHS INSTITUTE-WISE RESPORT
        */
        if ( last12MonthsStats ) {
            const last12MonthsStatsRows = [[{ value: "Last 12 months activity in each institute", align: "right", fontWeight: "bold", span: 3 }, null, null]]
            for ( const institute in last12MonthsStats ) {
                last12MonthsStatsRows.push(
                    [{ value: institute, fontWeight: "bold", align: "center", span: 3, backgroundColor: "#FFA500" }, null, null],
                    [{ value: "MONTH", fontWeight: "bold" }, { value: "CLASS SESSIONS", fontWeight: "bold", align: "right" }, { value: "RESOURCES LINKED", fontWeight: "bold", align: "right" },]
                )
                for ( const month in last12MonthsStats[institute] ) {
                    if ( Object.prototype.hasOwnProperty.call( last12MonthsStats[institute], month ) ) {
                        const monthStats = last12MonthsStats[institute][month]
                        last12MonthsStatsRows.push( [
                            { value: month, fontWeight: "bold", fontStyle: "italic" },
                            { value: monthStats.live_sessions, type: Number },
                            { value: monthStats.resources_linked, type: Number },
                        ] )
                    }
                }
                last12MonthsStatsRows.push( [] )
            }
            excelSheets.push( "Last-12-months-stats" )
            excelSheetsData.push( last12MonthsStatsRows )
        }

        const excelData = await writeXlsxFile(
            excelSheetsData,
            {
                sheets: excelSheets,
                stickyRowsCount: 1,
                buffer: true
            } )

        const blob = new Blob( [excelData] )
        const a = document.createElement( 'a' )
        a.download = `${name}.xlsx`
        a.href = window.URL.createObjectURL( blob )
        a.click()
    } catch ( err ) {
        console.log( err )
        toast( "Ooops! Something went wrong while exporting the report!" )
    }
}

export const exportDiscussionsAuditReport = async ( data, last12MonthsStats, name ) => {
    try {
        /*
        // REPORT COUNTS (PRE-PROCESSING)
        */
        let aggregation = {}
        let totalDiscussions = 0
        let totalComments = 0
        let totalViews = 0
        let excelSheets = []
        let excelSheetsData = []

        data.forEach( item => {
            const { _id: { institute }, count, views, comments, upvotes, downvotes } = item

            if ( !aggregation[institute] ) {
                aggregation[institute] = {
                    institute,
                    totalDiscussions: 0,
                    totalComments: 0,
                    totalViews: 0,
                    totalUpvotes: 0,
                    totalDownvotes: 0,
                }
            }

            // Add to institute total
            aggregation[institute].totalDiscussions += +count
            aggregation[institute].totalComments += +comments
            aggregation[institute].totalViews += +views
            aggregation[institute].totalUpvotes += +upvotes
            aggregation[institute].totalDownvotes += +downvotes

            totalDiscussions += +count
            totalComments += +comments
            totalViews += +views
        } )
        aggregation = sortArrayOfObjectsByKey( Object.values( aggregation ), 'totalDiscussions', -1, "number" )
        /*
        // REPORT COUNTS (PRE-PROCESSING-ENDS)
        */


        /*
        // USER-WISE DISCUSSION STATS ROWS
        */
        const userWiseDiscusonStatsRow = [[
            { fontWeight: 'bold', value: "SL. NO." },
            { value: "POSTED BY", fontWeight: 'bold' },
            { fontWeight: 'bold', value: "AUID/EMPCODE" },
            { value: "INSTITUTE", fontWeight: 'bold' },
            { value: "DISCUSSIONS", fontWeight: 'bold', align: "right" },
            { value: "VIEWS", fontWeight: 'bold', align: "right" },
            { value: "COMMENTS", fontWeight: 'bold', align: "right" },
            { value: "TOTAL VOTES", fontWeight: 'bold', align: "right" },
            { value: "UP VOTES", fontWeight: 'bold', align: "right" },
            { value: "DOWN VOTES", fontWeight: 'bold', align: "right" },
        ]]

        for ( let i = 0; i < data.length; i++ ) {
            const record = data[i]
            let row = []
            row.push( { value: i + 1, type: Number } )
            row.push( { value: capitalizeWords( record?._id?.name, "lowercase" ) || record?._id.created_by, type: String } )
            row.push( { value: record._id.created_by, type: String } )
            row.push( { value: record?._id?.institute, type: String } )
            row.push( { value: +record.count, type: Number } )
            row.push( { value: +record.views, type: Number } )
            row.push( { value: +record.comments, type: Number } )
            row.push( { value: +record.votes, type: Number } )
            row.push( { value: +record.upvotes, type: Number } )
            row.push( { value: +record.downvotes, type: Number } )
            userWiseDiscusonStatsRow.push( row )
        }

        userWiseDiscusonStatsRow.push( [{ value: "TOTAL", fontWeight: "bold", span: 4, align: "right" }, null, null, null, { value: totalDiscussions, type: Number, fontWeight: "bold" }, { value: totalViews, type: Number, fontWeight: "bold" }, { value: totalComments, type: Number, fontWeight: "bold" }, null, null, null] )
        excelSheetsData.push( userWiseDiscusonStatsRow )
        excelSheets.push( "User-wise-discussions-stats" )

        /*
        // INSTITUTE-WISE DISCUSSION STATS ROWS
        */
        const granualityReportRows = [[
            { value: 'INSTITUTE', fontWeight: 'bold', width: 20 },
            { value: 'DISCUSSIONS', fontWeight: 'bold', align: "right", width: 20 },
            { value: 'COMMENTS', fontWeight: 'bold', align: "right", width: 20 },
            { value: 'VIEWS', fontWeight: 'bold', align: "right", width: 20 }
        ]]

        for ( const instituteData of aggregation ) {
            granualityReportRows.push( [
                { value: instituteData.institute },
                { value: `${instituteData.totalDiscussions} ( ${roundToNDecimalPoints( ( instituteData.totalDiscussions * 100 ) / totalDiscussions )}% )`, type: String, align: "right" },
                { value: `${instituteData.totalComments} ( ${roundToNDecimalPoints( ( instituteData.totalComments * 100 ) / totalComments )}% )`, type: String, align: "right" },
                { value: `${instituteData.totalViews} ( ${roundToNDecimalPoints( ( instituteData.totalViews * 100 ) / totalViews )}% )`, type: String, align: "right" },
            ] )

        }
        // GRAND TOTAL
        granualityReportRows.push( [], [
            { value: 'TOTAL', fontWeight: 'bold' },
            { value: totalDiscussions, type: Number, fontWeight: 'bold' },
            { value: totalComments, type: Number, fontWeight: 'bold' },
            { value: totalViews, type: Number, fontWeight: 'bold' }
        ] )
        excelSheetsData.push( granualityReportRows )
        excelSheets.push( "Institute-wise-stats" )


        /*
        // INSTITUTE-WISE LAST 12 MONTHS DISCUSSION STATS ROWS
        */
        if ( last12MonthsStats ) {
            const last12MonthsStatsRows = [[{ value: "Last 12 months activity in each institute", align: "right", fontWeight: "bold", span: 3 }, null, null]]
            for ( const institute in last12MonthsStats ) {
                last12MonthsStatsRows.push(
                    [{ value: institute, fontWeight: "bold", align: "center", span: 3, backgroundColor: "#FFA500" }, null, null],
                    [{ value: "MONTH", fontWeight: "bold" }, { value: "DISCUSSIONS", fontWeight: "bold", align: "right" }, { value: "COMMENTS", fontWeight: "bold", align: "right" },]
                )
                for ( const month in last12MonthsStats[institute] ) {
                    if ( Object.prototype.hasOwnProperty.call( last12MonthsStats[institute], month ) ) {
                        const monthStats = last12MonthsStats[institute][month]
                        last12MonthsStatsRows.push( [
                            { value: month, fontWeight: "bold", fontStyle: "italic" },
                            { value: monthStats.discussions, type: Number },
                            { value: monthStats.comments, type: Number },
                        ] )
                    }
                }
                last12MonthsStatsRows.push( [] )
            }
            excelSheetsData.push( last12MonthsStatsRows )
            excelSheets.push( "Last-12-months-stats" )
        }

        const excelData = await writeXlsxFile(
            excelSheetsData,
            {
                sheets: excelSheets,
                stickyRowsCount: 1,
                buffer: true
            } )


        const blob = new Blob( [excelData] )
        const a = document.createElement( 'a' )
        a.download = `${name}.xlsx`
        a.href = window.URL.createObjectURL( blob )
        a.click()
    } catch ( err ) {
        console.log( err )
        toast( "Ooops! Something went wrong while exporting the report!" )
    }
}